import { useEffect, useState } from 'react';
import { Flex } from '@chakra-ui/react';
import DateFilter from '@inovua/reactdatagrid-enterprise/DateFilter';
import DataTable, { ActionsRenderer, booleanRenderer, dateRenderer, RowProps, dateSort } from 'components/DataTable';
import {
  useTableData,
  useToast,
  useDeleteRole,
  useFetchRoles,
  useCreateRole,
  useUpdateRole,
  useBulkDeleteRoles,
  useFetchProducts,
} from 'hooks';
import {
  errorMessageRolesDefaultChange,
  errorMessageRolesAdminChange,
  errorMessageRolesAdminDelete,
  errorMessageRolesDefaultDelete,
  errorMessageRolesAnonymousDelete,
  errorMessageRolesAnonymousChange,
} from 'constants/errors';
import { Button, Modal, ModalBody, ModalHeader } from 'components/UI';
import { ActionLoadingCallback } from 'components/DataTable/Actions/Action';
import { checkIfError } from 'utils/helpers';
import pageOptions from 'constants/pages';
import specificationOptions from 'constants/specifications';
import { ActionCallbackItem } from 'components/DataTable/Actions';
import { ISettingsValues } from 'types';
import { TypeRowProps } from '@inovua/reactdatagrid-enterprise/types';
import { IRoleResource, IRoleResourceWithUsers } from '../../../../server/types';
import Role from './Role/Role';
import { RoleFormValues } from './RoleForm/RoleForm';

const defaultColumnInfo = {
  defaultFlex: 1,
  minWidth: 160,
};

const columns = [
  {
    header: 'Name',
    name: 'name',
    ...defaultColumnInfo,
  },
  {
    header: 'Users',
    name: 'numAssignedUsers',
    ...defaultColumnInfo,
  },
  {
    header: 'Default',
    name: 'isDefault',
    render: booleanRenderer,
    ...defaultColumnInfo,
  },
  {
    header: 'Anonymous',
    name: 'isAnonymous',
    render: booleanRenderer,
    ...defaultColumnInfo,
  },
  {
    header: 'Created',
    name: 'createdAt',
    dateFormat: 'YYYY/MM/DD',
    operator: 'eq',
    type: 'date',
    sort: dateSort,
    filterEditor: DateFilter,
    render: dateRenderer,
    ...defaultColumnInfo,
  },
];

type RolesProps = {
  values: ISettingsValues | Record<string, never>;
};

const Roles = ({ values: settings }: RolesProps) => {
  const [tableData, setTableData, { deleteItem, editItem, editItems, addItems, bulkDeleteItems }] =
    useTableData<IRoleResourceWithUsers>();

  const [fetchRoles, { error: fetchRolesError, loading: fetchRolesLoading }] = useFetchRoles();
  const [createRole, { error: createError }] = useCreateRole();
  const [updateRole, { error: updateError }] = useUpdateRole();
  const [deleteRole, { error: deleteError }] = useDeleteRole();
  const [bulkDeleteRoles, { loading: bulkDeleteLoading, error: bulkDeleteError }] = useBulkDeleteRoles();
  const [fetchProducts, { error: fetchProductsError, data: products, loading: fetchProductsLoading }] =
    useFetchProducts();

  const [currentRole, setCurrentRole] = useState<Partial<IRoleResourceWithUsers>>();
  const [modalOpen, setModalOpen] = useState(false);
  const [showToast] = useToast();

  useEffect(() => {
    if (fetchRolesError || fetchProductsError || deleteError || createError || updateError) {
      const error = fetchRolesError ?? fetchProductsError ?? createError ?? updateError;

      if (!error) return;

      showToast({ message: error.toString(), status: 'error' });
    }
  }, [fetchRolesError, fetchProducts, createError, updateError, bulkDeleteError]);

  const handleCheckField = (
    ids: string[],
    message = errorMessageRolesDefaultChange,
    field: keyof RoleFormValues = 'isDefault'
  ) => {
    const isFieldActive = tableData.some(
      (tableItem: IRoleResource) => tableItem[field] === true && tableItem.id && ids.includes(tableItem.id)
    );

    const isOtherFieldActive = tableData
      .filter(({ id }) => !id || !ids.includes(id))
      .some((role) => role[field] === true);

    if (!isFieldActive || isOtherFieldActive) return true;

    showToast({
      message,
      status: 'error',
    });

    return false;
  };

  const handleClearDefaults = (values: Partial<RoleFormValues>) => {
    const nonDefaultRoleIds = tableData
      .filter((role) => role.isDefault === true && role.id !== currentRole?.id)
      .map(({ id }: IRoleResource) => id) as string[];

    if (!values.isDefault || nonDefaultRoleIds.length === 0) return;

    editItems(nonDefaultRoleIds as string[], { isDefault: false }, 'id');
  };

  const handleCheckAdmin = (ids: string[], message = errorMessageRolesAdminChange) => {
    const isAdmin = tableData.some(
      (tableItem: IRoleResource) =>
        tableItem.isAdmin === true && typeof tableItem.id !== 'undefined' && ids.includes(tableItem.id)
    );

    if (!isAdmin) return true;

    showToast({
      message,
      status: 'error',
    });

    return false;
  };

  const handleDelete = async (itemData: IRoleResource, _: IRoleResource, cb: ActionLoadingCallback) => {
    // Check if there's no other role with default truthy
    if (
      typeof itemData?.id === 'undefined' ||
      !handleCheckField([itemData?.id], errorMessageRolesDefaultDelete) ||
      !handleCheckAdmin([itemData?.id], errorMessageRolesAdminDelete) ||
      !handleCheckField([itemData?.id], errorMessageRolesAnonymousDelete, 'isAnonymous')
    )
      return;

    try {
      cb(true);

      const response = await deleteRole(itemData.id!);

      if (checkIfError(response)) {
        showToast({
          message: response.data?.message ?? 'Delete failed.',
          status: 'error',
        });

        return;
      }

      deleteItem(itemData.id!, 'id');

      showToast({
        message: `${itemData.name} deleted successfully.`,
      });
    } finally {
      cb(false);
    }
  };

  const handleClone = async (itemData: IRoleResource, _: IRoleResource, cb: ActionLoadingCallback) => {
    if (!itemData?.id) return;

    cb(true);

    const clone = {
      ...itemData,
      name: `${itemData.name.substring(0, 94)} Clone`,
      isDefault: false,
      isAnonymous: false,
    };
    const response = await createRole(clone);

    if (checkIfError(response)) return;

    addItems([{ ...clone, ...response, numAssignedUsers: 0 }]);

    showToast({
      message: `${itemData.name} cloned successfully.`,
    });

    cb(false);
  };

  const handleBulkDelete = async ({ ids }: ActionCallbackItem<IRoleResource>, cb: ActionLoadingCallback) => {
    // Check if there's no other role with default truthy

    if (!ids || ids.length === 0) {
      showToast({
        message: `No role(s) selected.`,
        status: 'warning',
      });
      return;
    }

    if (
      !handleCheckField(ids, errorMessageRolesDefaultDelete) ||
      !handleCheckField(ids, errorMessageRolesAnonymousDelete, 'isAnonymous') ||
      !handleCheckAdmin(ids, errorMessageRolesAdminDelete)
    )
      return;

    const response = await bulkDeleteRoles({ ids });

    cb();

    if (checkIfError(response)) {
      showToast({
        message: response.data?.message ?? 'Bulk delete failed.',
        status: 'error',
      });

      return;
    }

    bulkDeleteItems(ids, 'id');

    showToast({
      message: `${ids.length} role(s) deleted successfully.`,
    });
  };

  const handleLoadResources = async () => {
    const [rolesResult] = await Promise.all([fetchRoles(), fetchProducts()]);

    if (checkIfError(rolesResult)) return;

    setTableData(rolesResult);
  };

  const handleSubmitRole = async (values: RoleFormValues) => {
    const isDisablingDefaultForOnlyDefaultRole = !values.isDefault && !handleCheckField([currentRole?.id ?? '']);
    const isDisablingAnonymousForOnlyAnonymousRole =
      !values.isAnonymous &&
      !handleCheckField([currentRole?.id ?? ''], errorMessageRolesAnonymousChange, 'isAnonymous');

    if (isDisablingDefaultForOnlyDefaultRole || isDisablingAnonymousForOnlyAnonymousRole) return;

    const fn = !currentRole?.id ? createRole(values) : updateRole(currentRole.id, values);

    const response = await fn;

    if (checkIfError(response)) {
      return;
    }

    const index = tableData.findIndex((tableItem) => tableItem.id === currentRole?.id);

    handleClearDefaults(values);

    const roleWithUsers = { ...values, ...response, numAssignedUsers: currentRole?.numAssignedUsers ?? 0 };

    const updateTableDataFn = () => (currentRole ? editItem(index, roleWithUsers) : addItems([roleWithUsers]));

    updateTableDataFn();

    setModalOpen(false);

    showToast({
      message: currentRole ? `${currentRole.name} updated successfully.` : `${values.name} created successfully.`,
    });
  };

  const handleViewRole: (event?: MouseEvent, rowProps?: TypeRowProps | RowProps) => void = (
    event?: MouseEvent,
    data?: TypeRowProps | RowProps
  ) => {
    if (!handleCheckAdmin([data?.data.id])) {
      return;
    }

    setCurrentRole(data?.data ?? undefined);

    setModalOpen(true);
  };

  useEffect(() => {
    handleLoadResources();
  }, []);

  const inlineActions = [
    {
      type: 'clone',
      renderTitle: (data: IRoleResource) => `Clone ${data.name}?`,
      renderMessage: () => 'You are about to clone this item, do you want to proceed?',
      callback: handleClone,
    },
    {
      type: 'delete',
      renderTitle: (data: IRoleResource) => `Delete ${data.name}?`,
      renderMessage: () => 'You are about to permanently delete this item, do you want to proceed?',
      callback: handleDelete,
    },
  ];

  const tableActions = [
    {
      type: 'filter',
      callback: () => {},
    },
    {
      type: 'bulkDelete',
      loading: bulkDeleteLoading,
      callback: handleBulkDelete,
    },
  ];

  const pageActions = (
    <Flex w="full" alignItems="center" justifyContent="space-between">
      <Button onClick={() => handleViewRole()}>Create Role</Button>
    </Flex>
  );

  return (
    <>
      <DataTable<IRoleResource>
        id="role-archive-3"
        rows={tableData}
        columns={[
          ...columns.filter(({ name }) => !['created', 'statusFormat'].includes(name)),
          {
            id: '$actions',
            header: 'Actions',
            render: (renderProps) => <ActionsRenderer<IRoleResource> {...renderProps} actions={inlineActions} />,
          },
        ]}
        tableStyles={{ minHeight: 'calc(100% - 126px)' }}
        tableContainerProps={{ minW: '100%' }}
        pageActions={pageActions}
        loading={fetchRolesLoading || fetchProductsLoading}
        actions={tableActions}
        onRowDoubleClick={handleViewRole}
        tableProps={{ idProperty: 'id' }}
      />
      <Modal
        closeOnEsc={false}
        closeOnOverlayClick={false}
        size="2xl"
        isCentered
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
      >
        <ModalHeader textAlign="center" fontWeight="normal" my={2} fontSize="4xl">
          {currentRole ? `Update ${currentRole.name}` : 'Create a Role'}
        </ModalHeader>
        <ModalBody pb={8}>
          <Role
            onSubmit={handleSubmitRole}
            data={currentRole}
            settings={settings}
            pageOptions={
              pageOptions.map(({ id: value, text: label }) => ({
                value,
                label,
              })) ?? []
            }
            productOptions={
              !checkIfError(products) && products?.products
                ? products.products
                    .map(({ id: value, displayName: label }) => ({
                      value: value!,
                      label,
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label))
                : []
            }
            specificationOptions={specificationOptions.map(({ id: value, text: label }) => ({ value, label }))}
          />
        </ModalBody>
      </Modal>
    </>
  );
};

export default Roles;
